import React, { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Icon,
  InlineGrid,
  InlineStack,
  Link,
  Page,
  Select,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonPage,
  Tabs,
  Text,
  TextField,
  useBreakpoints,
} from '@shopify/polaris'
import { useTranslation } from 'react-i18next'
import {
  ChevronRightIcon,
  ChevronUpIcon,
  StatusActiveIcon,
} from '@shopify/polaris-icons'

import { numberFormater } from '../../utils/formater'
import { useDashboardDetails } from '../../hooks/useDashboardDetails'
import PlanDowngradeFeedbackForm from '../../components/PlanDowngrafeFeedbackForm'
import { useRedirect } from '../../hooks'
import queryClient from '../../utils/query'
import { openChat } from '../../components/ExternalScripts'
import { areFeaturesEnabled } from '../../utils/features'
import { FEATURES } from '../../constants/features'
import { WIDGET_TYPES } from '../../constants/widgets'
import { sendToHubspot } from '../../utils/feedback'
import * as PAGES from '../../constants/page'
import { invalidateShopStatus } from '../../hooks/useShopStatus'
import analytics, { MixPanelAnalytics } from '../../utils/analytics'
import { PricingStrategies, PlanSlugs } from '../../constants/enums'

function Pricing({
  plans,
  skipPage = false,
  isLoading = false,
  addPromoCode,
  changePlanCB,
  dashboardDetails
}) {
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    invalidate,
  } = dashboardDetails || useDashboardDetails()
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isAnnual, setAnnual] = useState(
    dashboardData?.shopPlan?.onAnnual || false,
  )
  const breakpoints = useBreakpoints()
  
  // Filter plans based on pricing strategy
  let filteredPlans = plans.filter((plan) =>
    isAnnual ? plan.hasAnnualPrice === isAnnual : true,
  )
  
  // If shop config is ORDER_COUNT strategy, don't show free plans that are also ORDER_COUNT
  // This prevents showing free ORDER_COUNT plans to ORDER_COUNT shops
  if (dashboardData?.config?.pricingStrategy === PricingStrategies.ORDER_COUNT) {
    filteredPlans = filteredPlans.filter(plan => {
      // Filter out free plans that have ORDER_COUNT strategy
      if (plan.isFree && plan.strategy === PricingStrategies.ORDER_COUNT) {
        return false
      }
      return true
    })
  }
  
  const { redirectToLink } = useRedirect()
  const liveSupportEnabled = areFeaturesEnabled(
    [FEATURES.LIVE_SUPPORT],
    dashboardData,
  )

  const codeSubmitHandler = useCallback(async () => {
    if (!code) {
      setCodeError(t('PricingModal.errors.codeEmpty'))
      return
    }
    setLoading(true)
    await addPromoCode(code)
    setLoading(false)
  }, [code, t])

  const updateCode = useCallback(
    (value) => {
      setCode(value)
      setCodeError(false)
    },
    [setCode, setCodeError],
  )

  const changePlan = useCallback(async ({ plan, promo, onAnnual }) => {
    const url = await changePlanCB({ plan, promo, onAnnual })
    if (url) {
      redirectToLink({
        url,
        external: true,
      })
    }
    await invalidateShopStatus()
    setTimeout(() => {
      invalidate()
    }, 200)
  }, [])

  const selectOptions = [
    {
      label: t('PricingModal.monthlyPlan'),
      value: 'monthly',
    },
    {
      label: t('PricingModal.annualPlan'),
      value: 'annual',
    },
  ]

  if (isLoading || dashboardLoading) {
    return <Skeleton />
  }

  if (plans.length === 0) {
    return (
      <div
        style={{
          height: 100,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box padding={'300'}>
          <Text>
            {t('PricingModal.noPlans', {
              link: (
                <>
                  {liveSupportEnabled && (
                    <>
                      <Link onClick={openChat} monochrome>
                        {t('PricingModal.liveSupport')}
                      </Link>
                      {t('PricingModal.or')}
                    </>
                  )}
                  <Link monochrome>{t('PricingModal.contactUs')}</Link>
                </>
              ),
            })}
          </Text>
        </Box>
      </div>
    )
  }

  const children = (
    <Box padding={'300'}>
      <BlockStack gap={'400'}>
        {skipPage && (
          <InlineGrid columns={'1fr auto'} alignItems="start">
            <Text as="p">
              {t('PricingModal.description', {
                selectionText: (
                  <Link monochrome url="/pricing">
                    {t('PricingModal.selectionText')}
                  </Link>
                ),
              })}
            </Text>
            <InlineStack gap={'300'} align="start">
              <InlineStack gap={'200'} align="start">
                <TextField
                  placeholder={t('PricingModal.promotionPlaceholder')}
                  value={code}
                  onChange={updateCode}
                  error={codeError}
                />
                <div>
                  <Button
                    variant="primary"
                    onClick={codeSubmitHandler}
                    loading={loading}
                  >
                    {t('PricingModal.promotionCta')}
                  </Button>
                </div>
              </InlineStack>
            </InlineStack>
          </InlineGrid>
        )}
        <InlineGrid
          alignItems="start"
          gap={'300'}
          columns={
            breakpoints.lgUp
              ? Array(filteredPlans.length).fill('1fr').join(' ')
              : '1fr'
          }
        >
          {filteredPlans.map((plan) => {
            const isCurrentPlan =
              plan.id == dashboardData.shopPlan.plan &&
              isAnnual === dashboardData.shopPlan.onAnnual
            const price = isAnnual ? plan.annualPrice : plan.price
            const isDowngrade =
              parseInt(price) < parseInt(dashboardData?.shopPlan?.price)
            return (
              <PlanCard
                showAnnual={isAnnual}
                isDowngrade={isDowngrade}
                onPlanChange={(selectedStep) =>
                  changePlan({
                    plan: plan.slug,
                    promo: code,
                    onAnnual: isAnnual,
                    selectedStep: selectedStep,
                  })
                }
                isCurrentPlan={isCurrentPlan}
                horizontal={filteredPlans.length === 1}
                plan={plan}
                key={plan.id}
                isPopular={
                  dashboardData?.shop?.plan === 'shopify_plus' 
                    ? plan.slug === PlanSlugs.SCALE
                    : plan.slug === PlanSlugs.PRO
                }
                promotionData={plan.promotionData ?? {}}
                dashboardData={dashboardData}
              />
            )
          })}
        </InlineGrid>
        
        {/* Show cancel subscription link for ORDER_COUNT shops on paid plans */}
        {dashboardData?.config?.pricingStrategy === PricingStrategies.ORDER_COUNT &&
         dashboardData?.shopPlan?.strategy === PricingStrategies.ORDER_COUNT &&
         parseFloat(dashboardData?.shopPlan?.price) > 0 && (
          <Box>
            <InlineStack align="center" gap="200">
              <Text tone="subdued">{t('PricingModal.cancelSubscription.question')}</Text>
              <Button
                variant="plain"
                onClick={() =>
                  changePlan({
                    plan: PlanSlugs.FREE,
                    promo: '',
                    onAnnual: false,
                  })
                }
              >
                {t('PricingModal.cancelSubscription.button')}
              </Button>
            </InlineStack>
          </Box>
        )}
        
        <div
          style={{
            padding: 'var(--p-space-300)',
          }}
        />
      </BlockStack>
    </Box>
  )

  if (skipPage) {
    return children
  }

  const showSelector = plans.some((plan) => plan.hasAnnualPrice)

  return (
    <Page
      backAction={{
        url: '/',
      }}
      fullWidth
      title={t('PricingPage.title')}
      subtitle={
        <>
          {t('PricingModal.description', {
            selectionText: (
              <Link url="/pricing" monochrome>
                {t('PricingModal.selectionText')}
              </Link>
            ),
          })}
        </>
      }
      primaryAction={
        <InlineGrid columns={'auto auto'} alignItems="center" gap={'200'}>
          {showSelector && (
            <Select
              options={selectOptions}
              value={isAnnual ? 'annual' : 'monthly'}
              onChange={(val) => {
                setAnnual(val === 'annual')
              }}
            />
          )}
          <InlineStack gap={'200'} align="start">
            <TextField
              placeholder={t('PricingModal.promotionPlaceholder')}
              value={code}
              onChange={updateCode}
              error={codeError}
            />
            <div>
              <Button
                variant="primary"
                onClick={codeSubmitHandler}
                loading={loading}
              >
                {t('PricingModal.promotionCta')}
              </Button>
            </div>
          </InlineStack>
        </InlineGrid>
      }
    >
      {children}
      <div
        style={{
          padding: 'var(--p-space-400)',
        }}
      />
    </Page>
  )
}

export default Pricing

function PlanCard({
  plan,
  horizontal = false,
  isPopular = false,
  isCurrentPlan,
  isDowngrade: isDowngradeProp,
  promotionData,
  onPlanChange,
  showAnnual,
  dashboardData
}) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [feedbackModalOpen, setFeebackModalOpen] = useState(false)
  const [selectedStep, setSelectedStep] = useState(0)
  
  // For display, show the total price (base + first step) for ORDER_COUNT plans
  let displayPrice = showAnnual ? plan.annualPrice : plan.price
  if (plan.strategy === PricingStrategies.ORDER_COUNT && plan.steps && plan.steps.length > 0) {
    // Show base price + first step's price
    const basePrice = parseFloat(plan.price || 0)
    const firstStepPrice = parseFloat(plan.steps[0].price || 0)
    displayPrice = basePrice + firstStepPrice
  }
  
  // For downgrade calculation, use the estimated total price for ORDER_COUNT plans
  let actualPrice = displayPrice
  if (plan.strategy === PricingStrategies.ORDER_COUNT && plan.steps && plan.steps.length > 0) {
    const basePrice = parseFloat(plan.price || 0)
    const currentStep = plan.steps[selectedStep] || plan.steps[0]
    const stepPrice = parseFloat(currentStep.price || 0)
    actualPrice = basePrice + stepPrice
  }
  
  // Recalculate isDowngrade based on actual price
  const isDowngrade = isDowngradeProp || parseInt(actualPrice) < parseInt(dashboardData?.shopPlan?.price)
  
  // Use the plan's own strategy, not the shop's global strategy
  let featureEnabled = plan.featuresEnabled.filter((feature) => {
    const ignoredFeatures = [FEATURES.FUNNELS]
    return !ignoredFeatures.includes(feature)
  })
  featureEnabled = FeaturesOrder.filter((feature) =>
    featureEnabled.some((f) => f === feature),
  )
  plan.pagesEnabled = PagesOrder.filter((page) =>
    plan.pagesEnabled.some((p) => p === page),
  )
  plan.recommendationsEnabled = RecommendationOrder.filter((widget) =>
    plan.recommendationsEnabled.some((w) => w === widget),
  )
  let prevPrice = null,
    currentPrice = displayPrice,  // Use display price, not actual price
    promoText = null

  if (promotionData?.price) {
    prevPrice = showAnnual ? plan.annualPrice : plan.price
    currentPrice =
      (showAnnual ? promotionData.annualPrice ?? null : promotionData.price) ??
      0
    promoText = t('PricingModal.PlanTemplate.discountPromoHeroTextTemplate', {
      price: (parseFloat(prevPrice) - parseFloat(currentPrice)).toFixed(2),
      promoCode: promotionData.code,
    })
  } else if (promotionData?.trialDays) {
    promoText = t('PricingModal.PlanTemplate.trialPromoHeroTextTemplate', {
      days: promotionData.trialDays,
      promoCode: promotionData.code,
    })
  }

  const feedbackHandler = async (values) => {
    const { error } = await sendToHubspot(values)
    if (error) {
      return {
        error,
      }
    }
    analytics.trackPlanInitEvent(MixPanelAnalytics.Actions.UPGRADE_INITIATED,{
      previousPlan: dashboardData.shopPlan.plan,
      previousPrice: dashboardData.shopPlan.price,
      updatedPlan: plan.id,
      updatedPrice: plan.price,
      reason: values.reason,
      otherReason: values.otherReason,
      feedback: values.feedback,
      trial: new Date(dashboardData.shopPlan.trialEndsAt).valueOf() > new Date().valueOf()
    })
    await onPlanChange(selectedStep)
    setFeebackModalOpen(false)
    return {
      data: 'success',
      error: null,
    }
  }

  const clickHandler = async () => {
    setLoading(true)
    if (isDowngrade) {
      setFeebackModalOpen(true)
    } else {
      await onPlanChange(selectedStep)
      analytics.trackPlanInitEvent(MixPanelAnalytics.Actions.UPGRADE_INITIATED,{
        previousPlan: dashboardData.shopPlan.plan,
        previousPrice: dashboardData.shopPlan.price,
        updatedPlan: plan.id,
        updatedPrice: plan.price,
        trial: new Date(dashboardData.shopPlan.trialEndsAt).valueOf() > new Date().valueOf()
      })
    }
    setLoading(false)
  }

  const content = (showBadge = false) => {
    // For ORDER_COUNT plans, only show features with enhanced design
    if (plan.strategy === PricingStrategies.ORDER_COUNT) {
      return (
        <FeatureShowcase
          features={featureEnabled}
          isPopular={isPopular}
          horizontal={horizontal}
          showBadge={showBadge}
        />
      )
    }
    
    // For other plans, show features, widgets, and pages
    return (
      <>
        <FeatureShowcase
          features={featureEnabled}
          isPopular={isPopular}
          horizontal={horizontal}
          showBadge={showBadge}
        />
        <ListStack
          title={t('PricingModal.PlanTemplate.Widget.title')}
          items={plan.recommendationsEnabled.map((widget) =>
            t(`Widgets.${widget}.label`),
          )}
          collapsed={!showBadge}
          badgeContent={
            !showBadge && `+${plan.recommendationsEnabled.length - 3}`
          }
          darkTheme={!horizontal && isPopular}
        />
        <ListStack
          title={t('PricingModal.PlanTemplate.Pages.title')}
          items={plan.pagesEnabled.map((page) => t(`Pages.${page}.label`))}
          badgeContent={
            !showBadge &&
            plan.pagesEnabled.length > 3 &&
            `+${plan.pagesEnabled.length - 3}`
          }
          collapsed={!showBadge}
          darkTheme={!horizontal && isPopular}
        />
      </>
    )
  }

  const maxPrice = parseFloat(
    (
      (parseFloat(plan.recommendationServesBatchPrice) *
        (parseInt(plan.recommendationsServedHardLimit) -
          parseInt(plan.maximumRecommendationsServed))) /
      parseInt(plan.recommendationServesBatchSize)
    ).toFixed(2),
  )

  const renderOrderCountEstimator = () => {
    if (!plan.steps || plan.steps.length === 0) return null;
    
    const currentStep = plan.steps[selectedStep] || plan.steps[0];
    const basePrice = parseFloat(plan.price || 0);
    const stepPrice = parseFloat(currentStep.price || 0);
    const totalPrice = basePrice + stepPrice;
    const isFreeTier = totalPrice === 0;
    
    return (
      <Box padding="200" background="bg-surface-secondary" borderRadius="200">
        <BlockStack gap="200">
          <Text variant="bodyMd" fontWeight="semibold">
            {t('PricingModal.PlanTemplate.OrderCount.estimatePrice')}
          </Text>

          <BlockStack gap="150">
            <InlineStack align="space-between">
              <Text variant="bodySm">{t('PricingModal.PlanTemplate.OrderCount.selectVolume')}</Text>
              <Text variant="bodySm" fontWeight="semibold">
                {numberFormater(currentStep.min)} - {numberFormater(currentStep.max)}{t('PricingModal.PlanTemplate.OrderCount.ordersPerMonth')}
              </Text>
            </InlineStack>

            <div style={{ paddingBlock: 'var(--p-space-150)' }}>
              <input
                type="range"
                min="0"
                max={plan.steps.length - 1}
                value={selectedStep}
                onChange={(e) => setSelectedStep(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  height: '4px',
                  borderRadius: '2px',
                  background: `linear-gradient(to right, var(--p-color-bg-fill-brand) 0%, var(--p-color-bg-fill-brand) ${(selectedStep / (plan.steps.length - 1)) * 100}%, var(--p-color-border-subdued) ${(selectedStep / (plan.steps.length - 1)) * 100}%, var(--p-color-border-subdued) 100%)`,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
            </div>

            <InlineStack align="space-between">
              <Text variant="bodyMd">{t('PricingModal.PlanTemplate.OrderCount.estimatedCharge')}</Text>
              <Text variant="headingLg" fontWeight="semibold" tone={isFreeTier ? "success" : undefined}>
                {isFreeTier ? t('PricingModal.PlanTemplate.OrderCount.free') : `$${totalPrice}`}
              </Text>
            </InlineStack>
          </BlockStack>

          <BlockStack gap="100">
            {isFreeTier ? (
              <Text variant="bodySm" tone="subdued">
                {t('PricingModal.PlanTemplate.OrderCount.noChargesUpTo', { max: numberFormater(currentStep.max) })}
              </Text>
            ) : (
              <>
                <Text variant="bodySm" tone="subdued">
                  {t('PricingModal.PlanTemplate.OrderCount.basedOn', { min: numberFormater(currentStep.min), max: numberFormater(currentStep.max) })}
                </Text>
                {basePrice > 0 && (
                  <Text variant="bodySm" tone="subdued">
                    {t('PricingModal.PlanTemplate.OrderCount.includesCharges', { base: basePrice, step: stepPrice })}
                  </Text>
                )}
              </>
            )}
            <Text variant="bodySm" tone="subdued">
              {t('PricingModal.PlanTemplate.OrderCount.actualChargesNote')}
            </Text>
            {plan.coveredProducts && (
              <Text variant="bodySm" tone="subdued">
                {t('PricingModal.PlanTemplate.OrderCount.supportsProducts', { count: numberFormater(plan.coveredProducts) })}
              </Text>
            )}
          </BlockStack>
        </BlockStack>
      </Box>
    );
  };

  const getPlanNote = () => {
    const textColor = !horizontal && isPopular ? 'white' : undefined;
    const highlightColor = !horizontal && isPopular ? '#C4FAD0' : undefined;
    
    if (plan.isFree) {
      return t('PricingModal.PlanTemplate.Widget.notUsageBased');
    }
    
    // Use plan.strategy directly for determining display format
    switch (plan.strategy || PricingStrategies.SERVES) {
      case PricingStrategies.ORDER_COUNT:
        // For ORDER_COUNT, we'll show the slider instead of a note
        return null;
      
      case PricingStrategies.REVENUE_ATTRIBUTION:
        return t('PricingModal.PlanTemplate.Widget.revenueNote', {
          revenuePercent: (
            <strong style={{ color: highlightColor }}>
              {plan.revenuePercent}
            </strong>
          ),
          coveredRevenue: (
            <strong style={{ color: highlightColor }}>
              {plan.coveredRevenue}
            </strong>
          ),
          maxPrice: (
            <strong>
              {plan.maximumPrice || 'unlimited'}
            </strong>
          ),
        });
      
      case PricingStrategies.SERVES:
      default:
        if (plan.isUsageBased) {
          return t('PricingModal.PlanTemplate.Widget.note', {
            pricePer1000Widgets: (
              <strong style={{ color: highlightColor }}>
                {numberFormater(plan.recommendationServesBatchSize)}
              </strong>
            ),
            rate: (
              <strong style={{ color: highlightColor }}>
                ${plan.recommendationServesBatchPrice} /{' '}
                {numberFormater(plan.recommendationServesBatchSize)}
              </strong>
            ),
            maxWidgets: (
              <strong style={{ color: highlightColor }}>
                {numberFormater(plan.maximumRecommendationsServed)}
              </strong>
            ),
            maxPrice: (
              <strong>
                $
                {plan.maximumPrice
                  ? Math.min(parseFloat(plan.maximumPrice), maxPrice)
                  : maxPrice}
              </strong>
            ),
            maxWidgetsThreshold: (
              <strong style={{ color: highlightColor }}>
                {numberFormater(plan.recommendationsServedHardLimit)}
              </strong>
            ),
          });
        }
        return t('PricingModal.PlanTemplate.Widget.notUsageBased');
    }
  };

  const planNote = (
    <Text
      tone={!horizontal && isPopular ? 'text-inverse-secondary' : 'subdued'}
      as="p"
      variant="bodyMd"
    >
      <span
        style={{
          color: !horizontal && isPopular ? 'white' : undefined,
        }}
      >
        {getPlanNote()}
      </span>
    </Text>
  )
  const trialNote = (
    <Text tone={isPopular ? 'text-inverse-secondary' : 'base'}>
    <span
      style={{
        color: isPopular ? 'white' : undefined,
      }}
    >
      {t('PricingModal.PlanTemplate.Widget.trialNote', {
        trialDays: (
          <strong>
            {numberFormater(plan.trialDays)}
          </strong>
        ),
      })}
    </span>
  </Text>
  )

  if (horizontal) {
    return (
      <Box
        borderRadius="200"
        borderColor={'border'}
        borderWidth={'025'}
        padding={'0'}
        background="bg-fill"
      >
        <InlineGrid columns={'1fr 2fr'} alignItems="start" gap={'400'}>
          <div
            style={{
              borderRight:
                'var(--p-border-width-025) solid var(--p-color-border)',
              height: '100%',
            }}
          >
            <Box padding={'400'}>
              <BlockStack gap={'400'}>
                <BlockStack gap={'100'}>
                  <InlineGrid columns={'1fr auto'}>
                    <Text as="strong" variant="headingLg">
                      {plan.name}
                    </Text>
                    <InlineStack blockAlign="end">
                      <Text as="strong" variant="headingLg">
                        {currentPrice == 0 || currentPrice === '0' ? t('PricingModal.PlanTemplate.free') : `$${currentPrice}`}
                      </Text>
                      <Text>
                        {showAnnual
                          ? t('PricingModal.PlanTemplate.Widget.annualRate')
                          : t('PricingModal.PlanTemplate.Widget.rate')}
                      </Text>
                    </InlineStack>
                  </InlineGrid>
                  <InlineGrid columns={'1fr auto'}>
                    <Text>
                      {plan.strategy === PricingStrategies.ORDER_COUNT
                        ? (
                          plan.steps && plan.steps.length > 0 ? (
                            <>
                              <strong>{numberFormater(plan.steps[0].min)} - {numberFormater(plan.steps[0].max)}</strong>{t('PricingModal.PlanTemplate.OrderCount.ordersPerMonth')}
                              {plan.coveredProducts && (
                                <> • {t('PricingModal.PlanTemplate.OrderCount.upTo')}<strong>{numberFormater(plan.coveredProducts)}</strong>{t('PricingModal.PlanTemplate.OrderCount.products')}</>
                              )}
                            </>
                          ) : (
                            <>
                              <strong>{numberFormater(plan.coveredOrders || 0)}</strong>{t('PricingModal.PlanTemplate.OrderCount.ordersIncluded')}
                              {plan.coveredProducts && (
                                <> • {t('PricingModal.PlanTemplate.OrderCount.upTo')}<strong>{numberFormater(plan.coveredProducts)}</strong>{t('PricingModal.PlanTemplate.OrderCount.products')}</>
                              )}
                            </>
                          )
                        )
                        : t('PricingModal.PlanTemplate.Widget.subtitle', {
                            widgetsQuantity: (
                              <strong>
                                {numberFormater(plan.maximumRecommendationsServed)}
                              </strong>
                            ),
                          })
                      }
                    </Text>
                    <Text>
                      {plan.trialDays > 0 ? (
                        <Text variant="bodySm" tone="success">
                          {t('PricingModal.PlanTemplate.Widget.trialNote', {
                            trialDays: (
                              <strong>
                                {numberFormater(plan.trialDays)}
                              </strong>
                            ),
                          })}
                        </Text>
                      ) : (
                        prevPrice && (
                          <Text
                            variant="bodyLg"
                            textDecorationLine="line-through"
                          >
                            {prevPrice}
                            {showAnnual
                              ? t('PricingModal.PlanTemplate.Widget.annualRate')
                              : t('PricingModal.PlanTemplate.Widget.rate')}
                          </Text>
                        )
                      )}
                    </Text>
                  </InlineGrid>
                  {promoText && (
                    <div
                      style={{
                        background: isPopular ? '#63CE8E2B' : '#63CE8E33',
                        textAlign: 'center',
                        padding:
                          'var(--p-space-100) var(--p-space-200) var(--p-space-100) var(--p-space-200)',
                        borderRadius: 'var(--p-border-radius-200)',
                        margin: 'var(--p-space-200) 0 0 0',
                      }}
                    >
                      <Text as="h3" variant="headingSm">
                        <span
                          style={{
                            color: isPopular
                              ? 'white'
                              : 'var(--p-color-text-success)',
                          }}
                        >
                          {promoText}
                        </span>
                      </Text>
                    </div>
                  )}
                </BlockStack>
                {/* Show plan note for non-ORDER_COUNT plans */}
                {plan.strategy !== PricingStrategies.ORDER_COUNT && planNote}
                
                <Button
                  loading={loading}
                  onClick={clickHandler}
                  disabled={isCurrentPlan}
                  variant={(() => {
                    if (isDowngrade) {
                      return isPopular ? 'primary' : 'secondary'
                    }
                    return isPopular ? 'secondary' : 'primary'
                  })()}
                  size="large"
                >
                  {isCurrentPlan
                    ? t('PricingModal.PlanTemplate.currentPlan')
                    : isDowngrade
                      ? t('PricingModal.PlanTemplate.downgradeCta')
                      : t('PricingModal.PlanTemplate.buyCta')}
                </Button>
                
                {/* Note for ORDER_COUNT plans */}
                {plan.strategy === PricingStrategies.ORDER_COUNT && (
                  <Text variant="bodySm" tone="subdued" alignment="center">
                    {t('PricingModal.PlanTemplate.OrderCount.usageBasedNote')}
                  </Text>
                )}
                
                {/* Estimate Price section for ORDER_COUNT plans */}
                {plan.strategy === PricingStrategies.ORDER_COUNT && plan.steps && plan.steps.length > 0 && 
                  renderOrderCountEstimator()
                }
              </BlockStack>
            </Box>
          </div>
          <Box padding={'400'}>
            {content(true)}
          </Box>
        </InlineGrid>
        <PlanDowngradeFeedbackForm
          submitCallback={feedbackHandler}
          open={feedbackModalOpen}
          setOpen={setFeebackModalOpen}
        />
      </Box>
    )
  }

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        padding: 'var(--p-space-300)',
        background: isPopular
          ? 'var(--p-color-bg-fill-inverse)'
          : 'var(--p-color-bg-fill)',
        borderRadius: 'var(--p-border-radius-200)',
        border: `solid var(--p-border-width-${
          isPopular ? '0' : '025'
        }) var(--p-color-${isPopular ? 'transparent' : 'border'})`,
      }}
    >
      <BlockStack gap={'300'}>
        <BlockStack gap={'100'}>
          <InlineGrid columns={'1fr auto'}>
            <Text
              as="strong"
              tone={isPopular ? 'caution' : 'base'}
              variant="headingLg"
            >
              <span
                style={{
                  color: isPopular
                    ? 'var(--p-color-bg-fill-warning)'
                    : undefined,
                }}
              >
                {plan.name}
              </span>
            </Text>
            <InlineStack blockAlign="end">
              <Text
                as="strong"
                tone={isPopular ? 'text-inverse' : 'base'}
                variant="headingLg"
              >
                <span
                  style={{
                    color: isPopular ? 'white' : undefined,
                  }}
                >
                  {currentPrice == 0 || currentPrice === '0' ? t('PricingModal.PlanTemplate.free') : `$${currentPrice}`}
                </span>
              </Text>
              <Text tone={isPopular ? 'text-inverse' : 'base'}>
                <span
                  style={{
                    color: isPopular ? 'white' : undefined,
                  }}
                >
                  {showAnnual
                    ? t('PricingModal.PlanTemplate.Widget.annualRate')
                    : t('PricingModal.PlanTemplate.Widget.rate')}
                </span>
              </Text>
            </InlineStack>
          </InlineGrid>
          <InlineGrid columns={'1fr auto'}>
            <Text tone={isPopular ? 'text-inverse-secondary' : 'base'}>
              <span
                style={{
                  color: isPopular ? 'white' : undefined,
                }}
              >
                {plan.strategy === PricingStrategies.ORDER_COUNT
                  ? (
                    plan.steps && plan.steps.length > 0 ? (
                      <>
                        <strong>{numberFormater(plan.steps[0].min)} - {numberFormater(plan.steps[0].max)}</strong>{t('PricingModal.PlanTemplate.OrderCount.ordersPerMonth')}
                        {plan.coveredProducts && (
                          <> • {t('PricingModal.PlanTemplate.OrderCount.upTo')}<strong>{numberFormater(plan.coveredProducts)}</strong>{t('PricingModal.PlanTemplate.OrderCount.products')}</>
                        )}
                      </>
                    ) : (
                      <>
                        <strong>{numberFormater(plan.coveredOrders || 0)}</strong>{t('PricingModal.PlanTemplate.OrderCount.ordersIncluded')}
                        {plan.coveredProducts && (
                          <> • {t('PricingModal.PlanTemplate.OrderCount.upTo')}<strong>{numberFormater(plan.coveredProducts)}</strong>{t('PricingModal.PlanTemplate.OrderCount.products')}</>
                        )}
                      </>
                    )
                  )
                  : t('PricingModal.PlanTemplate.Widget.subtitle', {
                      widgetsQuantity: (
                        <strong>
                          {numberFormater(plan.maximumRecommendationsServed)}
                        </strong>
                      ),
                    })
                }
              </span>
            </Text>
            <Text tone={isPopular ? 'text-inverse-secondary' : 'base'}>
              {plan.trialDays > 0 ? (
                <Text variant="bodySm" tone={isPopular ? 'text-inverse-secondary' : 'success'}>
                  <span
                    style={{
                      color: isPopular ? 'white' : undefined,
                    }}
                  >
                    {t('PricingModal.PlanTemplate.Widget.trialNote', {
                      trialDays: (
                        <strong>
                          {numberFormater(plan.trialDays)}
                        </strong>
                      ),
                    })}
                  </span>
                </Text>
              ) : (
                prevPrice && (
                  <Text variant="bodyLg" textDecorationLine="line-through">
                    <span
                      style={{
                        color: isPopular ? 'white' : undefined,
                      }}
                    >
                      ${prevPrice}
                      {showAnnual
                        ? t('PricingModal.PlanTemplate.Widget.annualRate')
                        : t('PricingModal.PlanTemplate.Widget.rate')}
                    </span>
                  </Text>
                )
              )}
            </Text>
          </InlineGrid>
          {promoText && (
            <div
              style={{
                background: isPopular ? '#63CE8E2B' : '#63CE8E33',
                textAlign: 'center',
                padding:
                  'var(--p-space-100) var(--p-space-200) var(--p-space-100) var(--p-space-200)',
                borderRadius: 'var(--p-border-radius-200)',
                margin: 'var(--p-space-200) 0 0 0',
              }}
            >
              <Text as="h3" variant="headingSm">
                <span
                  style={{
                    color: isPopular ? 'white' : 'var(--p-color-text-success)',
                  }}
                >
                  {promoText}
                </span>
              </Text>
            </div>
          )}
        </BlockStack>
        {/* Show plan note for non-ORDER_COUNT plans */}
        {plan.strategy !== PricingStrategies.ORDER_COUNT && planNote}
        
        <Button
          loading={loading}
          onClick={clickHandler}
          disabled={isCurrentPlan}
          variant={(() => {
            if (isDowngrade) {
              return isPopular ? 'primary' : 'secondary'
            }
            return isPopular ? 'secondary' : 'primary'
          })()}
          size="large"
        >
          {isCurrentPlan
            ? t('PricingModal.PlanTemplate.currentPlan')
            : isDowngrade
              ? t('PricingModal.PlanTemplate.downgradeCta')
              : t('PricingModal.PlanTemplate.buyCta')}
        </Button>
        
        {/* Note for ORDER_COUNT plans */}
        {plan.strategy === PricingStrategies.ORDER_COUNT && (
          <Text variant="bodySm" tone="subdued" alignment="center">
            {t('PricingModal.PlanTemplate.OrderCount.usageBasedNote')}
          </Text>
        )}
        
        {/* Estimate Price section for ORDER_COUNT plans */}
        {plan.strategy === PricingStrategies.ORDER_COUNT && plan.steps && plan.steps.length > 0 && 
          renderOrderCountEstimator()
        }
        <BlockStack gap={'300'}>{content(false)}</BlockStack>
      </BlockStack>
      {isPopular && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            transform: 'translateY(-50%)',
            left: 'var(--p-space-300)',
          }}
        >
          <Badge tone="warning-strong">
            {t('PricingPage.recommendedText')}
          </Badge>
        </div>
      )}
      <PlanDowngradeFeedbackForm
        submitCallback={feedbackHandler}
        open={feedbackModalOpen}
        setOpen={setFeebackModalOpen}
      />
    </div>
  )
}

// New component for enhanced feature showcase
function FeatureShowcase({ features, isPopular, horizontal, showBadge }) {
  const { t } = useTranslation()
  const darkTheme = !horizontal && isPopular
  
  // Separate features into configured and unconfigured
  const configuredFeatures = features.filter(f => FeatureConfig[f])
  const unconfiguredFeatures = features.filter(f => !FeatureConfig[f])
  
  // Sort configured features strictly by FeaturesOrder
  const sortedConfiguredFeatures = configuredFeatures.sort((a, b) => {
    const aIndex = FeaturesOrder.indexOf(a)
    const bIndex = FeaturesOrder.indexOf(b)
    
    // If both are in FeaturesOrder, sort by their position
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex
    }
    
    // If only one is in FeaturesOrder, it comes first
    if (aIndex !== -1) return -1
    if (bIndex !== -1) return 1
    
    // If neither is in FeaturesOrder, maintain original order
    return 0
  })
  
  // Take up to 3 unconfigured features
  const extraFeatures = unconfiguredFeatures.slice(0, 3)
  
  const renderFeature = (feature, enhanced = true) => {
    const config = FeatureConfig[feature]
    
    if (!config) {
      // Unconfigured feature - show with basic styling but still prominent
      return (
        <Box 
          key={feature} 
          padding="300" 
          background={darkTheme ? 'bg-fill-transparent-secondary' : 'bg-surface-tertiary'} 
          borderRadius="200"
          borderColor={darkTheme ? 'border-inverse' : 'border-subdued'}
          borderWidth="025"
          minHeight="100%"
        >
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <div style={{ color: darkTheme ? '#4ade80' : '#16a34a', fontSize: '1.2em', flexShrink: 0 }}>✓</div>
            <Text variant="bodySm" fontWeight="medium">
              <span style={{ color: darkTheme ? 'white' : undefined }}>
                {t(`PricingModal.PlanTemplate.Features.${feature}`)}
              </span>
            </Text>
          </InlineStack>
        </Box>
      )
    }
    
    // All configured features get enhanced display
    return (
      <Box 
        key={feature} 
        padding="400" 
        background={
          config.priority === 'high' 
            ? (darkTheme ? 'bg-fill-brand-disabled' : 'bg-surface-brand-selected')
            : (darkTheme ? 'bg-fill-transparent-secondary' : 'bg-surface-secondary')
        }
        borderRadius="200"
        borderColor={
          config.priority === 'high'
            ? (darkTheme ? 'border-brand' : 'border-brand')
            : (darkTheme ? 'border-inverse' : 'border')
        }
        borderWidth="025"
        minHeight="100%"
      >
        <BlockStack gap="150">
          <InlineStack gap="300" blockAlign="center">
            <div style={{ fontSize: '1.5em', flexShrink: 0 }}>{config.icon}</div>
            <Text variant="headingSm" fontWeight="semibold">
              <span style={{ color: darkTheme ? 'white' : undefined }}>
                {t(`PricingModal.PlanTemplate.Features.${config.displayKey}`)}
              </span>
            </Text>
          </InlineStack>
          <Text variant="bodySm" tone={darkTheme ? undefined : 'subdued'}>
            <span style={{ 
              color: darkTheme ? '#e5e5e5' : undefined,
              fontSize: '0.9em',
              lineHeight: '1.4'
            }}>
              {t(`PricingModal.PlanTemplate.Features.${config.descriptionKey}`)}
            </span>
          </Text>
        </BlockStack>
      </Box>
    )
  }
  
  if (!showBadge) {
    // Collapsed view - show all configured features + up to 3 extras
    const featuresToShow = [...sortedConfiguredFeatures, ...extraFeatures]
    
    return (
      <BlockStack gap="300">
        <Text variant="headingMd" tone={darkTheme ? undefined : 'subdued'}>
          <span style={{ color: darkTheme ? 'white' : undefined }}>
            {t('PricingModal.PlanTemplate.Features.title')}
          </span>
        </Text>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: '12px',
          width: '100%'
        }}>
          {featuresToShow.map(feature => renderFeature(feature, true))}
        </div>
        
        {/* Only show badge if there are more unconfigured features beyond the 3 shown */}
        {unconfiguredFeatures.length > 3 && (
          <Badge tone={darkTheme ? 'info' : 'info'}>
            {t('PricingModal.PlanTemplate.moreFeatures', { count: unconfiguredFeatures.length - 3 })}
          </Badge>
        )}
      </BlockStack>
    )
  }
  
  // Expanded view - show all features
  const allFeaturesToShow = [...sortedConfiguredFeatures, ...extraFeatures]
  
  return (
    <BlockStack gap="400">
      <Text variant="headingMd" tone={darkTheme ? undefined : 'subdued'}>
        <span style={{ color: darkTheme ? 'white' : undefined }}>
          {t('PricingModal.PlanTemplate.Features.title')}
        </span>
      </Text>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        gap: '12px',
        width: '100%'
      }}>
        {allFeaturesToShow.map(feature => renderFeature(feature, true))}
      </div>
      
      {/* Only show badge if there are more unconfigured features beyond the 3 shown */}
      {unconfiguredFeatures.length > 3 && (
        <Badge tone={darkTheme ? 'info' : 'info'}>
          {t('PricingModal.PlanTemplate.additionalFeatures', { count: unconfiguredFeatures.length - 3 })}
        </Badge>
      )}
    </BlockStack>
  )
}

function ListStack({
  title,
  items,
  visibleInCollapse = 3,
  badgeContent,
  collapsed = true,
  darkTheme = false,
}) {
  const [sectionCollapsed, setSectionCollapsed] = useState(collapsed)
  return (
    <BlockStack gap={'100'} padding={'0'}>
      <InlineGrid columns={'1fr auto'} alignItems="center">
        <Text tone="subdued" variant="headingMd">
          <span
            style={{
              color: darkTheme ? 'white' : undefined,
            }}
          >
            {title}
          </span>
        </Text>
        {badgeContent && (
          <InlineStack>
            <Badge tone={darkTheme ? 'info-strong' : undefined}>
              {sectionCollapsed ? badgeContent : items.length}
            </Badge>
            <Button
              variant="plain"
              onClick={() => {
                setSectionCollapsed(!sectionCollapsed)
              }}
              icon={sectionCollapsed ? ChevronRightIcon : ChevronUpIcon}
            />
          </InlineStack>
        )}
      </InlineGrid>
      <BlockStack gap={'100'}>
        {items
          .slice(0, sectionCollapsed ? visibleInCollapse : undefined)
          .map((item, index) => {
            return (
              <InlineGrid key={index} columns={'auto 1fr'} gap={'100'} alignItems="start">
                <Icon
                  source={() => (
                    <StatusActiveIcon
                      style={{
                        color: darkTheme ? 'white' : undefined,
                      }}
                    />
                  )}
                />
                <Text>
                  <span
                    style={{
                      color: darkTheme ? 'white' : undefined,
                    }}
                  >
                    {item}
                  </span>
                </Text>
              </InlineGrid>
            )
          })}
      </BlockStack>
    </BlockStack>
  )
}

function Skeleton() {
  const { t } = useTranslation()
  return (
    <SkeletonPage fullWidth primaryAction title={t('PricingPage.title')}>
      <InlineGrid columns={3} gap={'300'}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index}>
            <BlockStack gap={'300'}>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={10} />
            </BlockStack>
          </Card>
        ))}
      </InlineGrid>
    </SkeletonPage>
  )
}


// Enhanced feature configuration with metadata
const FeatureConfig = {
  [FEATURES.BASIC_RECOMMENDATIONS]: {
    key: FEATURES.BASIC_RECOMMENDATIONS,
    icon: '🎯',
    priority: 'high',
    category: 'core',
    displayKey: 'basic_rec_display',
    descriptionKey: 'basic_rec_description'
  },
  [FEATURES.CHECKOUT_SECTIONS]: {
    key: FEATURES.CHECKOUT_SECTIONS,
    icon: '🛒',
    priority: 'high',
    category: 'conversion',
    displayKey: 'checkout_sections_display',
    descriptionKey: 'checkout_sections_description'
  },
  [FEATURES.BUNDLES]: {
    key: FEATURES.BUNDLES,
    icon: '📦',
    priority: 'high',
    category: 'revenue',
    displayKey: 'bundles_display',
    descriptionKey: 'bundles_description'
  },
  [FEATURES.AI_PRODUCT_ENRICHMENT]: {
    key: FEATURES.AI_PRODUCT_ENRICHMENT,
    icon: '🤖',
    priority: 'high',
    category: 'automation',
    displayKey: 'ai_product_enrichment_display',
    descriptionKey: 'ai_product_enrichment_description'
  },
  [FEATURES.CUSTOM_TEMPLATES]: {
    key: FEATURES.CUSTOM_TEMPLATES,
    icon: '🎨',
    priority: 'high',
    category: 'customization',
    displayKey: 'custom_templates_display',
    descriptionKey: 'custom_templates_description'
  },
  [FEATURES.MULTI_MARKET]: {
    key: FEATURES.MULTI_MARKET,
    icon: '🌍',
    priority: 'high',
    category: 'localization',
    displayKey: 'multi_market_display',
    descriptionKey: 'multi_market_description'
  },
  [FEATURES.SEGMENTS]: {
    key: FEATURES.SEGMENTS,
    icon: '🎯',
    priority: 'high',
    category: 'targeting',
    displayKey: 'segments_display',
    descriptionKey: 'segments_description'
  },
  [FEATURES.DEDICATED_ACCOUNT_MANAGER]: {
    key: FEATURES.DEDICATED_ACCOUNT_MANAGER,
    icon: '👔',
    priority: 'high',
    category: 'support',
    displayKey: 'dedicated_account_manager_display',
    descriptionKey: 'dedicated_account_manager_description'
  },
  [FEATURES.POST_PURCHASE]: {
    key: FEATURES.POST_PURCHASE,
    icon: '🎁',
    priority: 'medium',
    category: 'conversion',
    displayKey: 'post_checkout_display',
    descriptionKey: 'post_checkout_description'
  },
  [FEATURES.ACCOUNT_PAGE_PERSOLISATION]: {
    key: FEATURES.ACCOUNT_PAGE_PERSOLISATION,
    icon: '👤',
    priority: 'medium',
    category: 'personalization',
    displayKey: 'account_page_recommendations_display',
    descriptionKey: 'account_page_recommendations_description'
  },
  [FEATURES.AB_EXPERIENCE]: {
    key: FEATURES.AB_EXPERIENCE,
    icon: '🧪',
    priority: 'medium',
    category: 'optimization',
    displayKey: 'ab_experience_display',
    descriptionKey: 'ab_experience_description'
  },
  [FEATURES.LIVE_SUPPORT]: {
    key: FEATURES.LIVE_SUPPORT,
    icon: '💬',
    priority: 'medium',
    category: 'support',
    displayKey: 'live_support_display',
    descriptionKey: 'live_support_description'
  },
}

const FeaturesOrder = [
  FEATURES.BASIC_RECOMMENDATIONS,
  FEATURES.CHECKOUT_SECTIONS,
  FEATURES.BUNDLES,
  FEATURES.AI_PRODUCT_ENRICHMENT,
  FEATURES.CUSTOM_TEMPLATES,
  FEATURES.MULTI_MARKET,
  FEATURES.AB_EXPERIENCE,
  FEATURES.SEGMENTS,
  FEATURES.DEDICATED_ACCOUNT_MANAGER,
  FEATURES.POST_PURCHASE,
  FEATURES.ACCOUNT_PAGE_PERSOLISATION,
  FEATURES.LIVE_SUPPORT,
]

const RecommendationOrder = [
  WIDGET_TYPES.bought_together.value,
  WIDGET_TYPES.personalized.value,
  WIDGET_TYPES.similar_products.value,
  WIDGET_TYPES.cross_sell.value,
  WIDGET_TYPES.recently_viewed.value,
  WIDGET_TYPES.slots.value,
  WIDGET_TYPES.bestsellers.value,
  WIDGET_TYPES.trending.value,
  WIDGET_TYPES.recent_launch.value,
  WIDGET_TYPES.collections.value,
  WIDGET_TYPES.promoted.value,
  WIDGET_TYPES.custom.value,
]

const PagesOrder = [
  PAGES.HOME.value,
  PAGES.PRODUCT.value,
  PAGES.AJAX_CART.value,
  PAGES.CART.value,
  PAGES.COLLECTION.value,
  PAGES.BLOG.value,
  PAGES.OTHER.value,
  PAGES.NOT_FOUND.value,
  PAGES.RETURNS.value,
]
